import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { money, fmt, fmtDt, errMsg } from '../../utils/helpers';
import { Spinner, Modal, ConfirmModal, Field, Select } from '../../components/common/UI';
import { Plus, Trash2, Package, Layers, ChevronDown, ChevronRight, Truck, X, CheckCircle2, Info } from 'lucide-react';
import toast from 'react-hot-toast';

const PRICE_TYPE_OPTS = [
  { value: 'retail', label: 'РЦ' },
  { value: 'dealer', label: 'ДЦ' },
];

const fmtQty = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? String(Math.round(n)) : '0';
};


const calcDealerValue = (amount, dealerPct, priceType = 'retail') => {
  const value = Number(amount || 0);
  const pct = Number(dealerPct || 0);
  return priceType === 'retail' ? value * (1 - pct / 100) : value;
};

function FinanceStack({ orderValue, producerValue, compact = false }) {
  const orderAmount = Number(orderValue || 0);
  const producerAmount = Number(producerValue || 0);
  const marginAmount = orderAmount - producerAmount;
  const orderClass = compact ? 'text-[11px] text-gray-200 mono' : 'text-xs text-gray-100 mono';
  const producerClass = compact ? 'text-[11px] text-cyan-300 mono' : 'text-xs text-cyan-300 mono';
  const marginClass = compact ? 'text-[11px] text-emerald-300 mono' : 'text-xs text-emerald-300 mono';
  return (
    <div className="space-y-0.5 text-right">
      <div className={orderClass}>{money(orderAmount)}</div>
      <div className={producerClass}>{money(producerAmount)}</div>
      <div className={marginClass}>{money(marginAmount)}</div>
    </div>
  );
}


function HeaderHint({ label, lines, align = 'left' }) {
  const alignCls = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : '';
  const popupAlignCls = align === 'right'
    ? 'right-0'
    : align === 'center'
      ? 'left-1/2 -translate-x-1/2'
      : 'left-0';

  return (
    <span className={`relative inline-flex ${alignCls}`}>
      <span className="group inline-flex items-center gap-1 cursor-help select-none">
        <span>{label}</span>
        <Info size={12} className="text-gray-600" />
        <span className={`pointer-events-none invisible absolute top-full z-30 mt-2 w-max max-w-[320px] rounded-xl border border-gray-800 bg-gray-950/100 ring-1 ring-black/70 px-3 py-2 text-left shadow-2xl shadow-black/60 transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100 opacity-0 translate-y-1 ${popupAlignCls}`}>
          <span className="flex flex-col gap-1">
            {lines.map((line, idx) => (
              <span key={`${label}-${idx}`} className={line.className || 'text-gray-300'}>{line.text}</span>
            ))}
          </span>
        </span>
      </span>
    </span>
  );
}

function ProductPickerModal({ products, categories, title, onAdd, onClose, showPrices = true }) {
  const [cat, setCat] = useState('');
  const [productId, setProductId] = useState('');
  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [priceType, setPriceType] = useState('retail');
  const [unitPrice, setUnitPrice] = useState('');

  const catOpts = categories.map(c => ({ value: c.id, label: c.name }));
  const filteredOpts = (cat ? products.filter(p => p.category_id === +cat) : products)
    .map(p => ({ value: p.id, label: p.name + (p.sku ? ' [' + p.sku + ']' : '') }));

  const pick = (id) => {
    const prod = products.find(p => p.id === +id);
    setProductId(id);
    setProductName(prod ? prod.name : '');
    setUnitPrice(prod?.last_price || '');
  };

  const handleAdd = () => {
    if (showPrices) {
      if (!productName && !productId) { toast.error('Выберите или введите товар'); return; }
      if (!unitPrice && unitPrice !== 0) { toast.error('Укажите цену'); return; }
    } else if (!productId) {
      toast.error('Выберите товар из справочника'); return;
    }
    onAdd({ product_id: productId || null, product_name: productName, quantity, price_type: showPrices ? priceType : undefined, unit_price: showPrices ? unitPrice : undefined });
    setProductId(''); setProductName(''); setUnitPrice(''); setQuantity(1); setPriceType('retail');
  };

  return (
    <Modal title={title} onClose={onClose} size="md">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Категория">
            <Select value={cat} onChange={v => { setCat(v); setProductId(''); setProductName(''); setUnitPrice(''); }}
              options={catOpts} placeholder="Все категории" />
          </Field>
          <Field label="Товар из справочника">
            <Select value={productId} onChange={pick} options={filteredOpts} placeholder="Выберите товар" />
          </Field>
        </div>
        {showPrices ? (
          <Field label="Название" hint="Автозаполняется при выборе товара, или введите вручную">
            <input className="input" value={productName}
              onChange={e => { setProductName(e.target.value); if (!e.target.value) setProductId(''); }}
              placeholder="Или введите вручную" />
          </Field>
        ) : null}
        <div className={showPrices ? 'grid grid-cols-3 gap-3' : 'grid grid-cols-1 gap-3'}>
          <Field label="Кол-во">
            <input type="number" min="1" step="1" className="input" value={quantity}
              onChange={e => setQuantity(e.target.value)} />
          </Field>
          {showPrices && <Field label="Тип цены"><Select value={priceType} onChange={setPriceType} options={PRICE_TYPE_OPTS} /></Field>}
          {showPrices && <Field label="Цена, ₽" required><input type="number" min="0" step="0.01" className="input" value={unitPrice} onChange={e => setUnitPrice(e.target.value)} /></Field>}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-800">
          <button className="btn-secondary" onClick={onClose}>Закрыть</button>
          <button className="btn-primary" onClick={handleAdd}><Plus size={14} /> Добавить</button>
        </div>
      </div>
    </Modal>
  );
}

export function OrderItemsPanel({ orderId, orderStatus, canEditItems, dealerPct, showPrices = true, onRefresh, onOrderLocked }) {
  const { isProducer, isDealerAdmin, hasPerm } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expandedKits, setExpandedKits] = useState({});
  const [addProductModal, setAddProductModal] = useState(false);
  const [kitModal, setKitModal] = useState(null);
  const [addToKitModal, setAddToKitModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [kitForm, setKitForm] = useState({ kit_name: '', quantity: 1, notes: '' });
  const [kitLines, setKitLines] = useState([]);
  const [saving, setSaving] = useState(false);

  const [shippedMap, setShippedMap] = useState({});

  const load = useCallback(async () => {
    if (!orderId || orderId === 'new') { setLoading(false); return; }
    setLoading(true);
    try {
      const [{ data: itemsData }, { data: shipmentsData }] = await Promise.all([
        api.get('/orders/' + orderId + '/items'),
        api.get('/orders/' + orderId + '/shipments'),
      ]);
      setItems(itemsData);
      const map = {};
      shipmentsData.forEach(s => s.items?.forEach(si => {
        map[si.order_item_id] = (map[si.order_item_id] || 0) + +si.quantity;
      }));
      setShippedMap(map);
    } catch (err) { console.error(err); toast.error('Ошибка загрузки позиций'); }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const loadCatalog = async () => {
    const [{ data: p }, { data: c }] = await Promise.all([
      api.get('/catalog/products', { params: { active_only: 'true' } }),
      api.get('/catalog/categories'),
    ]);
    setProducts(p); setCategories(c);
  };

  const canEdit = !!canEditItems;
  const orderContentUnlocked = !orderStatus || orderStatus === 'placed';
  const dealerLocked = !isProducer && !orderContentUnlocked;
  const showDetailedFinance = showPrices && (isDealerAdmin || isProducer);

  const syncCurrentDealerOrder = useCallback(async (silent = false) => {
    if (isProducer || !orderId || orderId === 'new') return true;
    try {
      const { data } = await api.get('/orders/' + orderId);
      if (data?.status !== orderStatus) onOrderLocked?.(data);
      if (data?.status !== 'placed') {
        if (!silent) toast.error('Заказ уже взят в работу. Состав заказа заблокирован.');
        return false;
      }
      return true;
    } catch (err) {
      if (!silent) toast.error(errMsg(err));
      return false;
    }
  }, [isProducer, orderId, orderStatus, onOrderLocked]);

  useEffect(() => {
    if (isProducer || !orderId || orderId === 'new') return;
    const refreshState = () => { syncCurrentDealerOrder(true); };
    const timer = window.setInterval(refreshState, 15000);
    window.addEventListener('focus', refreshState);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshState);
    };
  }, [isProducer, orderId, syncCurrentDealerOrder]);

  const ensureCanChangeItems = async () => {
    if (!orderContentUnlocked) {
      toast.error('Заказ уже взят в работу. Состав заказа заблокирован.');
      return false;
    }
    if (isProducer) return true;
    return syncCurrentDealerOrder();
  };

  const openAddProduct = async () => { if (!(await ensureCanChangeItems())) return; await loadCatalog(); setAddProductModal(true); };

  const addProduct = async (picked) => {
    if (!(await ensureCanChangeItems())) return;
    setSaving(true);
    try {
      await api.post('/orders/' + orderId + '/items/product', picked);
      toast.success('Позиция добавлена');
      setAddProductModal(false);
      load(); onRefresh?.();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const openNewKit = async () => {
    if (!(await ensureCanChangeItems())) return;
    await loadCatalog();
    setKitForm({ kit_name: '', quantity: 1, notes: '' });
    setKitLines([]);
    setKitModal('new');
  };

  const openEditKit = async (kit) => {
    if (!(await ensureCanChangeItems())) return;
    await loadCatalog();
    setKitForm({ kit_name: kit.kit_name, quantity: kit.quantity, notes: kit.notes || '' });
    setKitLines((kit.kit_lines || []).map(l => ({
      _id: l.id, product_id: l.product_id, product_name: l.product_name,
      quantity: l.quantity, price_type: l.price_type, unit_price: l.unit_price,
    })));
    setKitModal(kit);
  };

  const addLineToKit = (picked) => {
    setKitLines(ls => [...ls, { _tempId: Date.now(), ...picked }]);
    setAddToKitModal(false);
  };

  const updateKitLine = (idx, field, value) => {
    setKitLines(ls => ls.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const removeKitLine = (idx) => setKitLines(ls => ls.filter((_, i) => i !== idx));

  const kitTotal = kitLines.reduce((s, l) => s + (+l.quantity || 0) * (+l.unit_price || 0), 0);

  const saveKit = async () => {
    if (!(await ensureCanChangeItems())) return;
    if (!kitForm.kit_name) { toast.error('Укажите название комплекта'); return; }
    if (!kitLines.length) { toast.error('Добавьте хотя бы одну позицию'); return; }
    setSaving(true);
    try {
      let kitId;
      if (kitModal === 'new') {
        const { data } = await api.post('/orders/' + orderId + '/items/kit', kitForm);
        kitId = data.id;
      } else {
        await api.patch('/orders/' + orderId + '/items/' + kitModal.id, kitForm);
        kitId = kitModal.id;
        const existingIds = kitLines.filter(l => l._id).map(l => l._id);
        for (const old of (kitModal.kit_lines || [])) {
          if (!existingIds.includes(old.id))
            await api.delete('/orders/' + orderId + '/items/' + kitId + '/lines/' + old.id);
        }
        for (const line of kitLines.filter(l => l._id))
          await api.patch('/orders/' + orderId + '/items/' + kitId + '/lines/' + line._id, line);
      }
      for (const line of kitLines.filter(l => !l._id))
        await api.post('/orders/' + orderId + '/items/' + kitId + '/lines', line);

      toast.success(kitModal === 'new' ? 'Комплект создан' : 'Обновлено');
      setKitModal(null);
      setExpandedKits(e => ({ ...e, [kitId]: true }));
      load(); onRefresh?.();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const deleteItem = async (item) => {
    if (!(await ensureCanChangeItems())) return;
    try { await api.delete('/orders/' + orderId + '/items/' + item.id); toast.success('Удалено'); load(); onRefresh?.(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const totalRetail = items.reduce((s, i) => {
    if (i.item_type === 'product') return s + +i.quantity * +i.unit_price;
    if (i.item_type === 'kit') return s + +i.quantity * (i.kit_total || 0);
    return s;
  }, 0);

  const totalDealer = items.reduce((s, i) => {
    if (i.item_type === 'product') {
      const line = +i.quantity * +i.unit_price;
      return s + (i.price_type === 'retail' ? line * (1 - (dealerPct || 0) / 100) : line);
    }
    if (i.item_type === 'kit') {
      return s + +i.quantity * (i.kit_lines || []).reduce((ks, l) => {
        const line = +l.quantity * +l.unit_price;
        return ks + (l.price_type === 'retail' ? line * (1 - (dealerPct || 0) / 100) : line);
      }, 0);
    }
    return s;
  }, 0);

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <div className="space-y-4">
      {!orderContentUnlocked && (
        <div className="rounded-lg border border-red-900 bg-red-950/35 px-4 py-3 text-sm text-red-200">
          Состав заказа заблокирован после перевода в статус «Изготовление».
        </div>
      )}
      {dealerLocked && (
        <div className="rounded-lg border border-amber-800/60 bg-amber-950/30 px-3 py-2 text-sm text-amber-300">
          Дилер не может менять позиции после взятия заказа в работу.
        </div>
      )}
      {canEdit && (
        <div className="flex gap-2">
          <button className="btn-primary" onClick={openAddProduct}><Plus size={14} /> Добавить товар</button>
          <button className="btn-secondary" onClick={openNewKit}><Layers size={14} /> Добавить комплект</button>
        </div>
      )}

      {!items.length ? (
        <p className="text-sm text-gray-600">Нет позиций — добавьте товары или комплекты</p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 uppercase tracking-wide px-1">
            <div className="col-span-5">Позиция</div>
            <div className="col-span-1 text-center">Кол</div>
            {showDetailedFinance ? (
              <>
                <div className="col-span-1 text-center">Тип</div>
                <div className="col-span-4 text-right">
                  <HeaderHint
                    align="right"
                    label="Финансы"
                    lines={[
                      { text: 'Цена заказа — стоимость для клиента.', className: 'text-gray-200' },
                      { text: 'Цена производителя — цена поставщика / себестоимость дилера.', className: 'text-cyan-300' },
                      { text: 'Маржа дилера — разница между ценой заказа и ценой производителя.', className: 'text-emerald-300' },
                    ]}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="col-span-2 text-center">{showPrices ? 'Тип' : ''}</div>
                <div className="col-span-2 text-right">{showPrices ? 'Цена ед.' : ''}</div>
                <div className="col-span-1 text-right">{showPrices ? 'Сумма' : ''}</div>
              </>
            )}
            <div className="col-span-1"></div>
          </div>

          {items.map(item => {
            const isKit = item.item_type === 'kit';
            const expanded = expandedKits[item.id];
            const linePrice = isKit ? (item.kit_total || 0) : +item.unit_price;
            const lineTotal = +item.quantity * linePrice;
            const producerLineTotal = isKit
              ? +item.quantity * (item.kit_lines || []).reduce((sum, line) => sum + calcDealerValue(+line.quantity * +line.unit_price, dealerPct, line.price_type), 0)
              : calcDealerValue(lineTotal, dealerPct, item.price_type);
            const marginLineTotal = lineTotal - producerLineTotal;
            return (
              <div key={item.id} className="bg-gray-800/60 border border-gray-800 rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 gap-2 items-center px-3 py-2.5">
                  <div className="col-span-5 flex items-center gap-2">
                    {isKit ? (
                      <button onClick={() => setExpandedKits(e => ({ ...e, [item.id]: !e[item.id] }))}
                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1 shrink-0">
                        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <Layers size={13} />
                      </button>
                    ) : <Package size={13} className="text-gray-600 ml-4 shrink-0" />}
                    <div>
                      <div className="text-sm text-gray-200 font-medium">{isKit ? item.kit_name : item.product_name}</div>
                      {!isKit && item.sku && <div className="text-xs text-gray-600 mono">{item.sku}</div>}
                      {isKit && <div className="text-xs text-gray-500">{item.kit_lines?.length || 0} поз.</div>}
                      {(() => {
                        const shipped = shippedMap[item.id] || 0;
                        if (!shipped) return null;
                        const all = shipped >= +item.quantity;
                        return <div className={'text-xs mt-0.5 ' + (all ? 'text-green-400' : 'text-yellow-400')}>
                          {all ? '✓ Отгружено' : 'Отгружено: ' + fmtQty(shipped) + ' / ' + fmtQty(item.quantity)}
                        </div>;
                      })()}
                    </div>
                  </div>
                  <div className="col-span-1 text-center text-sm text-gray-300">{fmtQty(item.quantity)}</div>
                  {showDetailedFinance ? (
                    <>
                      <div className="col-span-1 text-center">
                        {!isKit && (
                          <span className={'text-xs px-1.5 py-0.5 rounded ' + (item.price_type === 'retail' ? 'bg-orange-900/30 text-orange-300' : 'bg-gray-700 text-gray-400')}>
                            {item.price_type === 'retail' ? 'РЦ' : 'ДЦ'}
                          </span>
                        )}
                      </div>
                      <div className="col-span-4">
                        {isKit ? (
                          <div className={+item.quantity > 1 ? 'flex items-start justify-end gap-6' : 'flex items-start justify-end'}>
                            <div className="min-w-[110px]">
                              <div className="text-[10px] text-gray-500 text-right">1 комплект</div>
                              <FinanceStack compact orderValue={linePrice} producerValue={producerLineTotal / Math.max(+item.quantity || 1, 1)} />
                            </div>
                            {+item.quantity > 1 && (
                              <div className="min-w-[110px] border-l border-gray-800 pl-4">
                                <div className="text-[10px] text-gray-500 text-right">Всего × {fmtQty(item.quantity)}</div>
                                <FinanceStack compact orderValue={lineTotal} producerValue={producerLineTotal} />
                              </div>
                            )}
                          </div>
                        ) : (
                          <FinanceStack orderValue={lineTotal} producerValue={producerLineTotal} />
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="col-span-2 text-center">
                        {showPrices && !isKit && (
                          <span className={'text-xs px-1.5 py-0.5 rounded ' + (item.price_type === 'retail' ? 'bg-orange-900/30 text-orange-300' : 'bg-gray-700 text-gray-400')}>
                            {item.price_type === 'retail' ? 'РЦ' : 'ДЦ'}
                          </span>
                        )}
                      </div>
                      <div className="col-span-2 text-right mono text-sm text-gray-300">{showPrices ? money(linePrice) : ''}</div>
                      <div className="col-span-1 text-right mono text-sm font-medium text-gray-200">{showPrices ? money(lineTotal) : ''}</div>
                    </>
                  )}
                  <div className="col-span-1 flex justify-end gap-1">
                    {isKit && canEdit && (
                      <button className="btn-ghost p-1 text-blue-400 hover:text-blue-300" title="Редактировать комплект"
                        onClick={() => openEditKit(item)}><Layers size={13} /></button>
                    )}
                    {canEdit && (
                      <button className="btn-ghost p-1 hover:text-red-400" onClick={() => setDeleteConfirm(item)}>
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
                {isKit && expanded && (
                  <div className="border-t border-gray-700/50 bg-gray-900/30">
                    {!item.kit_lines?.length
                      ? <div className="px-8 py-3 text-xs text-gray-600">Комплект пуст</div>
                      : item.kit_lines.map(line => {
                          const componentOrderTotal = +line.quantity * +line.unit_price;
                          const componentProducerTotal = calcDealerValue(componentOrderTotal, dealerPct, line.price_type);
                          return (
                            <div key={line.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-gray-800/30 last:border-0">
                              <div className="col-span-5 pl-6 text-sm text-gray-400">↳ {line.product_name}</div>
                              <div className="col-span-1 text-center text-xs text-gray-500">{fmtQty(line.quantity)}</div>
                              {showDetailedFinance ? (
                                <>
                                  <div className="col-span-1 text-center">
                                    <span className={'text-xs px-1.5 py-0.5 rounded ' + (line.price_type === 'retail' ? 'bg-orange-900/20 text-orange-400' : 'bg-gray-700 text-gray-500')}>
                                      {line.price_type === 'retail' ? 'РЦ' : 'ДЦ'}
                                    </span>
                                  </div>
                                  <div className="col-span-5">
                                    <div className="flex items-start justify-end gap-4">
                                      <div className="text-right shrink-0">
                                        <div className="text-[10px] text-gray-500">Цена ед.</div>
                                        <div className="mono text-[11px] text-gray-300">{money(line.unit_price)}</div>
                                      </div>
                                      <div className="min-w-[170px]">
                                        <div className="text-[10px] text-gray-500 text-right">На 1 комплект</div>
                                        <FinanceStack compact orderValue={componentOrderTotal} producerValue={componentProducerTotal} />
                                      </div>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="col-span-2 text-center">
                                    {showPrices && <span className={'text-xs px-1.5 py-0.5 rounded ' + (line.price_type === 'retail' ? 'bg-orange-900/20 text-orange-400' : 'bg-gray-700 text-gray-500')}>
                                      {line.price_type === 'retail' ? 'РЦ' : 'ДЦ'}
                                    </span>}
                                  </div>
                                  <div className="col-span-2 text-right mono text-xs text-gray-400">{showPrices ? money(line.unit_price) : ''}</div>
                                  <div className="col-span-2 text-right mono text-xs text-gray-400">{showPrices ? money(componentOrderTotal) : ''}</div>
                                </>
                              )}
                            </div>
                          );
                        })
                    }
                    {showPrices && item.kit_lines?.length > 0 && (
                      <div className="px-3 py-2 flex items-start justify-between gap-3 text-xs bg-gray-800/20">
                        <div className="pl-6 text-gray-500 space-y-1">
                          <div>1 комплект: <span className="mono text-gray-300">{money(linePrice)}</span></div>
                          {+item.quantity > 1 && <div>× {fmtQty(item.quantity)} шт.</div>}
                        </div>
                        {showDetailedFinance ? (
                          <div className="min-w-[240px]">
                            <div className="text-[10px] text-gray-500 text-right mb-1">Итого по строке заказа</div>
                            <FinanceStack compact orderValue={lineTotal} producerValue={producerLineTotal} />
                          </div>
                        ) : (
                          <span className="mono text-gray-300">{money(lineTotal)}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {item.notes && <div className="border-t border-gray-700/30 px-3 py-1.5 text-xs text-gray-500 italic">{item.notes}</div>}
              </div>
            );
          })}

          {showPrices && (
            <div className="border-t border-gray-700 pt-3 space-y-1 text-sm">
              {showDetailedFinance ? (
                <div className="ml-auto max-w-md rounded-lg border border-gray-800 bg-gray-900/50 p-3">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-xs uppercase tracking-wide text-gray-500">Итого по позициям</div>
                    <HeaderHint
                      align="right"
                      label="Пояснение"
                      lines={[
                        { text: 'Цена заказа — стоимость для клиента.', className: 'text-gray-200' },
                        { text: 'Цена производителя — цена поставщика / себестоимость дилера.', className: 'text-cyan-300' },
                        { text: 'Маржа дилера — разница между ценой заказа и ценой производителя.', className: 'text-emerald-300' },
                      ]}
                    />
                  </div>
                  <FinanceStack orderValue={totalRetail} producerValue={totalDealer} />
                </div>
              ) : (
                <div className="flex justify-between font-medium"><span className="text-gray-400">Итого по позициям:</span><span className="mono text-gray-100">{money(totalRetail)}</span></div>
              )}
            </div>
          )}
        </div>
      )}

      {addProductModal && (
        <ProductPickerModal products={products} categories={categories}
          title="Добавить товар в заказ"
          showPrices={showPrices}
          onAdd={(picked) => { addProduct(picked); }}
          onClose={() => setAddProductModal(false)} />
      )}

      {kitModal && (
        <Modal title={kitModal === 'new' ? 'Новый комплект' : 'Редактировать: ' + kitModal.kit_name}
          onClose={() => setKitModal(null)} size="xl">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Field label="Название комплекта" required>
                  <input className="input" value={kitForm.kit_name} autoFocus
                    onChange={e => setKitForm(f => ({ ...f, kit_name: e.target.value }))} />
                </Field>
              </div>
              <Field label="Количество">
                <input type="number" min="1" step="1" className="input" value={kitForm.quantity}
                  onChange={e => setKitForm(f => ({ ...f, quantity: e.target.value }))} />
              </Field>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="label">Состав комплекта</span>
                <button className="btn-primary text-xs" onClick={() => setAddToKitModal(true)}>
                  <Plus size={12} /> Добавить позицию
                </button>
              </div>

              {!kitLines.length ? (
                <div className="text-sm text-gray-600 text-center py-8 border border-dashed border-gray-700 rounded-lg">
                  Нажмите «Добавить позицию» для наполнения комплекта
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 uppercase tracking-wide px-1 mb-1">
                    <div className="col-span-4">Товар</div>
                    <div className="col-span-2 text-center">Кол-во</div>
                    {showPrices && <div className="col-span-2 text-center">Тип</div>}
                    {showPrices && <div className="col-span-3 text-right">Цена / сумма</div>}
                    <div className="col-span-1"></div>
                  </div>
                  {kitLines.map((line, idx) => (
                    <div key={line._id || line._tempId || idx}
                      className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-800/60 rounded-lg border border-gray-800">
                      <div className={showPrices ? 'col-span-4 text-sm text-gray-300 truncate' : 'col-span-9 text-sm text-gray-300 truncate'}>{line.product_name}</div>
                      <div className="col-span-2">
                        <input type="number" min="0.001" step="1" className="input py-1 text-sm text-center"
                          value={line.quantity} onChange={e => updateKitLine(idx, 'quantity', e.target.value)} />
                      </div>
                      {showPrices && <div className="col-span-2"><Select value={line.price_type} onChange={v => updateKitLine(idx, 'price_type', v)} options={PRICE_TYPE_OPTS} /></div>}
                      {showPrices && <div className="col-span-3"><input type="number" min="0" step="0.01" className="input py-1 text-sm text-right" value={line.unit_price} onChange={e => updateKitLine(idx, 'unit_price', e.target.value)} /><div className="text-xs text-gray-600 text-right mt-0.5 mono">= {money(+line.quantity * +line.unit_price)}</div></div>}
                      <div className="col-span-1 flex justify-end">
                        <button className="btn-ghost p-1 hover:text-red-400" onClick={() => removeKitLine(idx)}>
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {showPrices && kitLines.length > 0 && (
              <div className="bg-gray-900 rounded-lg p-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">1 комплект:</span>
                  <span className="mono font-medium text-gray-200">{money(kitTotal)}</span>
                </div>
                {+kitForm.quantity > 1 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">× {kitForm.quantity} = итого:</span>
                    <span className="mono font-medium text-blue-300">{money(kitTotal * +kitForm.quantity)}</span>
                  </div>
                )}
                {dealerPct > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">К оплате дилером ({dealerPct}%):</span>
                    <span className="mono text-gray-400">{money(kitTotal * +kitForm.quantity * (1 - dealerPct / 100))}</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-800">
              <button className="btn-secondary" onClick={() => setKitModal(null)}>Отмена</button>
              <button className="btn-primary" onClick={saveKit} disabled={saving}>
                {saving ? <Spinner size={14} /> : (kitModal === 'new' ? 'Создать комплект' : 'Сохранить')}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {addToKitModal && (
        <ProductPickerModal products={products} categories={categories}
          title="Добавить позицию в комплект"
          showPrices={showPrices}
          onAdd={addLineToKit}
          onClose={() => setAddToKitModal(false)} />
      )}

      {deleteConfirm && (
        <ConfirmModal
          message={'Удалить "' + (deleteConfirm.item_type === 'kit' ? deleteConfirm.kit_name : deleteConfirm.product_name) + '"' + (deleteConfirm.item_type === 'kit' ? ' со всем составом?' : '?')}
          onConfirm={() => deleteItem(deleteConfirm)}
          onClose={() => setDeleteConfirm(null)} />
      )}
    </div>
  );
}

export function ShipmentsPanel({ orderId, onRefresh }) {
  const { isProducer } = useAuth();
  const [receivingId, setReceivingId] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ shipped_at: new Date().toISOString().slice(0, 10), note: '', items: {} });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [receiveConfirm, setReceiveConfirm] = useState(null);

  const load = useCallback(async () => {
    if (!orderId || orderId === 'new') { setLoading(false); return; }
    try {
      const [{ data: s }, { data: i }] = await Promise.all([
        api.get('/orders/' + orderId + '/shipments'),
        api.get('/orders/' + orderId + '/items'),
      ]);
      setShipments(s); setItems(i);
    } catch { } finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const shippedMap = {};
  shipments.forEach(s => s.items?.forEach(si => {
    shippedMap[si.order_item_id] = (shippedMap[si.order_item_id] || 0) + si.quantity;
  }));
  const availableItems = items.filter(i => (i.quantity - (shippedMap[i.id] || 0)) > 0);
  const allShipped = items.length > 0 && items.every(i => (shippedMap[i.id] || 0) >= i.quantity);

  const openModal = () => {
    const init = {};
    availableItems.forEach(i => { init[i.id] = 0; });
    setForm({ shipped_at: new Date().toISOString().slice(0, 10), note: '', items: init });
    setModal(true);
  };

  const createShipment = async () => {
    const shipItems = Object.entries(form.items).filter(([, q]) => +q > 0).map(([id, q]) => ({ order_item_id: +id, quantity: +q }));
    if (!shipItems.length) { toast.error('Укажите позиции'); return; }
    setSaving(true);
    try {
      await api.post('/orders/' + orderId + '/shipments', { shipped_at: form.shipped_at, note: form.note, items: shipItems });
      toast.success('Отгрузка создана'); setModal(false); load(); onRefresh?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const deleteShipment = async (id) => {
    try { await api.delete('/orders/' + orderId + '/shipments/' + id); toast.success('Удалено'); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const receiveShipment = async (id) => {
    setReceivingId(id);
    try {
      await api.patch('/orders/' + orderId + '/shipments/' + id + '/receive');
      toast.success('Получение подтверждено');
      load();
      onRefresh?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setReceivingId(null);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <div className="space-y-4">
      {isProducer && (
        <div className="flex items-center gap-3">
          <button className="btn-primary" onClick={openModal} disabled={availableItems.length === 0}>
            <Truck size={14} /> Создать отгрузку
          </button>
          {allShipped && items.length > 0 && <span className="text-xs text-green-400">✓ Всё отгружено</span>}
        </div>
      )}
      {!shipments.length ? <p className="text-sm text-gray-600">Отгрузок нет</p> : (
        <div className="space-y-3">
          {shipments.map(s => (
            <div key={s.id} className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <Truck size={15} className="text-purple-400" />
                    <span className="font-medium text-gray-200">Отгрузка {fmt(s.shipped_at)}</span>
                  </div>
                  {s.note && <div className="text-xs text-gray-500 ml-5">{s.note}</div>}
                  <div className="text-xs text-gray-600 ml-5">{fmtDt(s.created_at)} · {s.created_by_name}</div>
                  {s.received_at && (
                    <div className="text-xs text-emerald-400 ml-5">Получено: {fmtDt(s.received_at)}{s.received_by_name ? ' · ' + s.received_by_name : ''}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {s.received_at && (
                    <span className="text-xs px-2 py-1 rounded border border-emerald-700 bg-emerald-900/40 text-emerald-300">
                      Получено
                    </span>
                  )}
                  {!isProducer && !s.received_at && (
                    <button className="btn-secondary" onClick={() => setReceiveConfirm(s)} disabled={receivingId === s.id}>
                      {receivingId === s.id ? <Spinner size={14} /> : <><CheckCircle2 size={14} /> Подтвердить получение</>}
                    </button>
                  )}
                  {isProducer && (
                    <button className="btn-ghost p-1.5 hover:text-red-400" onClick={() => setDeleteConfirm(s)}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-1 pl-5">
                {s.items?.map(si => (
                  <div key={si.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-gray-300">
                      {si.item_type === 'kit' ? <Layers size={13} className="text-blue-400" /> : <Package size={13} className="text-gray-600" />}
                      {si.item_type === 'kit' ? si.kit_name : si.product_name}
                    </div>
                    <span className="text-gray-400 mono">× {fmtQty(si.quantity)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <Modal title="Создать отгрузку" onClose={() => setModal(false)} size="md">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Дата отгрузки">
                <input type="date" className="input" value={form.shipped_at}
                  onChange={e => setForm(f => ({ ...f, shipped_at: e.target.value }))} />
              </Field>
              <Field label="Примечание">
                <input className="input" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
              </Field>
            </div>
            <div>
              <div className="label mb-2">Позиции к отгрузке</div>
              <div className="space-y-2">
                {availableItems.map(item => {
                  const remaining = Math.max(0, Math.round(+item.quantity - (shippedMap[item.id] || 0)));
                  const val = form.items[item.id] ?? remaining;
                  return (
                    <div key={item.id} className="flex items-center gap-3 p-2.5 bg-gray-800/60 rounded-lg border border-gray-800">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm text-gray-200">
                          {item.item_type === 'kit' ? <Layers size={13} className="text-blue-400" /> : <Package size={13} className="text-gray-600" />}
                          {item.item_type === 'kit' ? item.kit_name : item.product_name}
                        </div>
                        <div className="text-xs text-gray-500 ml-5">Остаток: {fmtQty(remaining)} из {fmtQty(item.quantity)}</div>
                      </div>
                      <input type="number" min="0" max={remaining} step="1" className="input w-24 text-right py-1"
                        value={val} onChange={e => setForm(f => ({ ...f, items: { ...f.items, [item.id]: +e.target.value } }))} />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-secondary" onClick={() => setModal(false)}>Отмена</button>
              <button className="btn-primary" onClick={createShipment} disabled={saving}>
                {saving ? <Spinner size={14} /> : <><Truck size={14} /> Сохранить</>}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {receiveConfirm && (
        <Modal title="Подтвердить получение" onClose={() => setReceiveConfirm(null)} size="sm">
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-800/60 bg-amber-950/30 p-3">
              <div className="text-sm font-medium text-amber-300">Подтвердите получение отгрузки</div>
              <div className="mt-1 text-sm leading-6 text-gray-300">
                Нажимайте только если товар действительно получен. После подтверждения отменить отметку будет невозможно.
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button className="btn-secondary" onClick={() => setReceiveConfirm(null)}>Нет</button>
              <button
                className="btn-primary"
                onClick={async () => {
                  const id = receiveConfirm.id;
                  setReceiveConfirm(null);
                  await receiveShipment(id);
                }}
                disabled={receivingId === receiveConfirm.id}
              >
                {receivingId === receiveConfirm.id ? <Spinner size={14} /> : 'Да, подтвердить'}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {deleteConfirm && (
        <ConfirmModal message={'Удалить отгрузку от ' + fmt(deleteConfirm.shipped_at) + '?'}
          onConfirm={() => deleteShipment(deleteConfirm.id)} onClose={() => setDeleteConfirm(null)} />
      )}
    </div>
  );
}
